import React, { useState, useEffect } from 'react';
import axiosInstance from '../store/axiosInterceptor';
import { useToast } from '../components/Toast/ToastContext';
import { TableSkeleton } from '../components/Skeleton';

interface Session {
  id: string;
  booking_id: string;
  scheduled_time: string;
  date?: { day: number; month: string; year: number };
  time?: string;
  date_str?: string;
  time_str?: string;
  coach_name?: string;
  coach_avatar?: string;
  student_name: string;
  student_avatar?: string;
  status: 'upcoming' | 'completed' | 'coach_no_show' | 'student_no_show' | 'unscheduled';
  title: string;
  description?: string;
  notes?: string;
  joining_link?: string;
  meeting_platform?: string;
  duration?: number;
}

type TabType = 'All' | 'Upcoming' | 'Completed' | 'Student No-Show' | 'Coach No-Show';

const SessionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const statusMap: Record<TabType, string> = {
        'All': '',
        'Upcoming': 'upcoming',
        'Completed': 'completed',
        'Student No-Show': 'student_no_show',
        'Coach No-Show': 'coach_no_show',
      };

      const statusParam = statusMap[activeTab];
      const url = new URL('/api/admin/sessions', window.location.origin);
      
      if (statusParam) {
        url.searchParams.append('status', statusParam);
      }
      url.searchParams.append('page', '0');
      url.searchParams.append('limit', '100');

      const response = await axiosInstance.get<Session[]>(url.pathname + url.search);

      setSessions(response.data || []);
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch sessions';
      showToast(message, 'error');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-orange-50 text-orange-600';
      case 'completed':
        return 'bg-green-50 text-green-600';
      case 'coach_no_show':
        return 'bg-red-50 text-red-600';
      case 'student_no_show':
        return 'bg-red-50 text-red-600';
      case 'unscheduled':
        return 'bg-gray-50 text-gray-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'Upcoming';
      case 'completed':
        return 'Completed';
      case 'coach_no_show':
        return 'Coach No-Show';
      case 'student_no_show':
        return 'Student No-Show';
      case 'unscheduled':
        return 'Unscheduled';
      default:
        return status;
    }
  };

  const formatDateTime = (session: Session) => {
    try {
      // Use pre-formatted date and time from API if available
      if (session.date && session.time) {
        const formattedDate = `${session.date.month} ${session.date.day}, ${session.date.year}`;
        return { date: formattedDate, time: session.time };
      }
      
      // Fallback to date_str and time_str
      if (session.date_str && session.time_str) {
        return { date: session.date_str, time: session.time_str };
      }
      
      // Parse scheduled_time if no formatted values
      if (!session.scheduled_time) {
        return { date: 'Not scheduled', time: '-' };
      }
      
      const dateObj = new Date(session.scheduled_time);
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return { date: 'Invalid date', time: '-' };
      }
      
      return {
        date: dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        time: dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      };
    } catch {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  const filteredSessions = sessions.filter((session) => {
    const matchesSearch =
      !searchQuery ||
      (session.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-3">
        {(['All', 'Upcoming', 'Completed', 'Student No-Show', 'Coach No-Show'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative w-80">
        <input
          type="text"
          placeholder="Search People"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-300 focus:outline-none focus:ring-0"
        />
        <svg
          className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Sessions Table */}
      <div className="overflow-hidden rounded-xl border border-[#F2F4F7] bg-white shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Date</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Time</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Coach</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Student</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Status</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Focus area</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6">
                  <TableSkeleton rows={8} columns={6} />
                </td>
              </tr>
            ) : filteredSessions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center">
                  <p className="text-sm text-gray-500">No sessions found</p>
                </td>
              </tr>
            ) : (
              filteredSessions.map((session) => {
                const { date, time } = formatDateTime(session);
                return (
                  <tr 
                    key={session.id} 
                    className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-5 py-4 text-sm text-gray-900">{date}</td>
                    <td className="px-5 py-4 text-sm text-gray-900">{time}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {session.coach_avatar ? (
                          <img src={session.coach_avatar} alt={session.coach_name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                            {session.coach_name?.charAt(0) || 'C'}
                          </div>
                        )}
                        <span className="text-sm text-gray-900">{session.coach_name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {session.student_avatar ? (
                          <img src={session.student_avatar} alt={session.student_name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-red-400 flex items-center justify-center text-white text-sm font-medium">
                            {session.student_name?.charAt(0) || 'S'}
                          </div>
                        )}
                        <span className="text-sm text-gray-900">{session.student_name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${getStatusBadge(session.status)}`}>
                        {getStatusLabel(session.status)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900">{session.title || 'N/A'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SessionsPage;
